import { formatPhoneNumber, normalizePhoneInput } from "@/util/formatters";
import { FormInput } from "@frontend/components/elements/form/Form.styles";
import type { IntakeFormRichResponse } from "@frontend/components/intake/Intake";
import {
  EVENT_CATEGORIES,
  EVENT_CONTEXTS,
  trackEvent,
} from "@frontend/lib/analytics";
import { cn } from "@frontend/shared/lib/utils";
import { format, isValid, parse } from "date-fns";
import { Files } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  type Control,
  Controller,
  type ControllerRenderProps,
  type FieldError,
  type FieldErrors,
  type UseFormClearErrors,
  type UseFormGetValues,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormTrigger,
} from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import type { z } from "zod";
import { IntakeAddressInput } from "./IntakeAddressInput";
import {
  type ClinicalProviderFormData,
  IntakeClinicalProviderInput,
} from "./IntakeClinicalProviderInput";
import { IntakeDateInput } from "./IntakeDateInput";
import type { IntakeQuestionChoice, RefMap } from "./IntakeForm";
import type { fieldSchema } from "./IntakeFormTypes";
import { IntakeImageInput } from "./IntakeImageInput";
import { type IntakeListEntry, IntakeListInput } from "./IntakeListInput";
import IntakeMultiSelectInput from "./IntakeMultiSelectInput";
import {
  IntakeSignatureInput,
  TypeformIntakeSignatureInput,
} from "./IntakeSignatureDialogue";
import IntakeSingleSelectInput from "./IntakeSingleSelectInput";

const USE_THREE_PART_DATE_INPUT = false;

export const formatRequiredLabel = (
  text: string,
  isRequired: boolean,
  hideQuestionText?: boolean,
): React.ReactNode => {
  if (hideQuestionText) return null;
  if (!isRequired) return text;

  return (
    <>
      {text}
      <span
        className="required-asterisk text-base"
        style={{ color: "#00a3ff" }}
      >
        *
      </span>
    </>
  );
};

/**
 * Returns the correct form input based on the questionType
 *
 * @param field
 * @param error field level errors used differently from ERrors
 * @param errors submit level errors
 * @param id
 * @param question
 * @param register
 * @param getValue
 * @param setValue
 * @param trigger
 * @param control
 * @returns
 */
export const formQuestionResolver = (
  hiddenQuestions: Set<number>,
  setHiddenQuestions: React.Dispatch<React.SetStateAction<Set<number>>>,
  setAnsweredQuestions: ((newValue: number) => void) | undefined,
  field: ControllerRenderProps<Partial<any>, string>,
  error: FieldError | undefined,
  errors: FieldErrors<Partial<any>>,
  clearErrors: UseFormClearErrors<Partial<any>>,
  question: z.infer<typeof fieldSchema>,
  register: any,
  getValues: UseFormGetValues<Partial<any>>,
  setValue: UseFormSetValue<Partial<any>>,
  trigger: UseFormTrigger<Partial<any>>,
  control: Control<Partial<any>, any>,
  isAdmin: boolean,
  isPrint: boolean,
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void,
  isFormComplete?: boolean,
  useNewStyles?: boolean,
  singleQuestionCallback?: any,
  hideQuestionText?: boolean,
  allSignatureQuestions?: any[],
): React.JSX.Element => {
  const updateProgress = (updatedField: string, updatedValue: any) => {
    if (!setAnsweredQuestions) {
      return;
    }
    if (errors[updatedField]) {
      clearErrors(updatedField);
    }

    const newValues = { ...getValues(), [updatedField]: updatedValue };
    const answerCount = Object.values(newValues).filter((x) => !!x).length;
    setAnsweredQuestions(answerCount);
  };

  const {
    id,
    type: questionType,
    isRequired,
    questionText,
    questionNumber,
  } = question;

  const labelFormatted = formatRequiredLabel(
    questionText,
    isRequired,
    hideQuestionText,
  );
  const label = labelFormatted ?? questionText;

  switch (questionType) {
    case "TEXT":
      return (
        <FormInput
          {...field}
          onChange={(e) => {
            field.onChange(e);
            updateProgress(field.name, e.target.value);
          }}
          onBlur={() => {
            initiateAutosave(getValues());
          }}
          error={error !== undefined}
          $labelColor="#000000"
          type="text"
          label={label ?? ""}
          style={{ marginTop: "28px" }}
          key={id}
          data-cy={`intake-${questionText}-input`}
          name="text"
          placeholder={""} // need placeholders for new styles
          questionId={id}
          readonly={isAdmin}
          disabled={isFormComplete}
          inputClassNames={
            useNewStyles
              ? cn(
                  "font-medium rounded-full bg-[#F4F4F4] p-4 placeholder:font-light",
                  field.value?.length > 0
                    ? "bg-white border-black border"
                    : "border-none",
                )
              : ""
          }
        />
      );
    case "PHONE_NUMBER":
      return (
        <FormInput
          {...field}
          onBlur={() => {
            initiateAutosave(getValues());
          }}
          className={cn(
            "w-[180px] rounded-full px-2 py-1 bg-medGrey outline-none placeholder:text-xs",
            error && "border border-red-600",
          )}
          onChange={(e) => {
            const phone = normalizePhoneInput(e.target.value);
            setValue(field.name, phone);
            updateProgress(field.name, e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" || e.key === "Delete") {
              return;
            }
            const newChar = e.key;
            if (!/[\d+\-() .]/.test(newChar)) {
              e.preventDefault();
            }
          }}
          value={
            !field.value
              ? ""
              : isValidPhoneNumber(field.value)
                ? formatPhoneNumber(field.value)
                : field.value
          }
          error={error !== undefined}
          $labelColor="#000000"
          type="text"
          label={label}
          style={{ marginTop: "28px" }}
          key={id}
          data-cy={`intake-${questionText}-input`}
          name="phone"
          placeholder={""} // need placeholders for new styles
          questionId={id}
          readonly={isAdmin}
          disabled={isFormComplete}
          inputClassNames={
            useNewStyles
              ? cn(
                  "font-medium rounded-full bg-[#F4F4F4] p-4 placeholder:font-light",
                  field.value?.length > 0
                    ? "bg-white border-black border"
                    : "border-none",
                )
              : ""
          }
        />
      );
    case "SINGLE_SELECT":
      return (
        <IntakeSingleSelectInput
          hiddenQuestions={hiddenQuestions}
          setHiddenQuestions={setHiddenQuestions}
          updateProgress={updateProgress}
          question={question}
          register={register}
          getValues={getValues}
          setValue={setValue}
          isAdmin={isAdmin}
          isPrint={isPrint}
          field={field}
          initiateAutosave={initiateAutosave}
          disabled={isFormComplete}
          useNewStyles={useNewStyles}
          valueChangeCallback={singleQuestionCallback}
          hideQuestionText={hideQuestionText}
        />
      );
    case "MULTI_SELECT":
      return (
        <IntakeMultiSelectInput
          hiddenQuestions={hiddenQuestions}
          setHiddenQuestions={setHiddenQuestions}
          updateProgress={updateProgress}
          question={question}
          register={register}
          getValues={getValues}
          setValue={setValue}
          isAdmin={isAdmin}
          isPrint={isPrint}
          field={field}
          initiateAutosave={initiateAutosave}
          disabled={isFormComplete}
          useNewStyles={useNewStyles}
          hideQuestionText={hideQuestionText}
        />
      );
    case "DATE":
      return USE_THREE_PART_DATE_INPUT ? (
        <IntakeDateInput
          field={field}
          setValue={setValue}
          error={error}
          clearErrors={clearErrors}
          questionText={questionText}
          isRequired={isRequired}
          hideQuestionText={hideQuestionText}
        />
      ) : (
        <FormInput
          {...field}
          onChange={(e) => {
            field.onChange(e);
            updateProgress(field.name, e.target.value);
          }}
          onBlur={() => {
            initiateAutosave(getValues());
          }}
          error={error !== undefined}
          $labelColor="#000000"
          type="date"
          label={label}
          style={{ marginTop: "28px" }}
          key={id}
          data-cy={`intake-${questionText}-input`}
          name="date"
          placeholder={""} // need placeholders for new styles
          questionId={id}
          readonly={isAdmin}
          disabled={isFormComplete}
          className="w-full"
          inputClassNames={
            useNewStyles
              ? cn(
                  "font-medium rounded-full bg-[#F4F4F4] p-4 placeholder:font-light text-center flex items-center w-full",
                  field.value?.length > 0
                    ? "bg-white border-black border"
                    : "border-none",
                )
              : ""
          }
        />
      );
    case "SIGNATURE":
      return useNewStyles ? (
        <TypeformIntakeSignatureInput
          isRequired={isRequired}
          questionId={id}
          questionText={questionText}
          questionAttributes={question.questionAttributes}
          field={field}
          register={register}
          errors={errors}
          setValue={setValue}
          getValues={getValues}
          trigger={trigger}
          control={control}
          updateProgress={updateProgress}
          isAdmin={isAdmin}
          initiateAutosave={initiateAutosave}
          allSignatureQuestions={allSignatureQuestions ?? []}
          disabled={isFormComplete}
        />
      ) : (
        <IntakeSignatureInput
          isRequired={isRequired}
          questionId={id}
          questionText={questionText}
          questionAttributes={question.questionAttributes}
          field={field}
          register={register}
          errors={errors}
          setValue={setValue}
          getValues={getValues}
          trigger={trigger}
          control={control}
          updateProgress={updateProgress}
          isAdmin={isAdmin}
          initiateAutosave={initiateAutosave}
          disabled={isFormComplete}
        />
      );
    case "IMAGE":
      return (
        <IntakeImageInput
          isRequired={isRequired}
          questionId={id}
          questionText={questionText}
          field={field}
          register={register}
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          trigger={trigger}
          control={control}
          updateProgress={updateProgress}
          isAdmin={isAdmin}
          initiateAutosave={initiateAutosave}
          disabled={isFormComplete}
          hideQuestionText={hideQuestionText}
        />
      );
    case "LONG_TEXT":
      return (
        <div className="flex flex-col w-full gap-1" key={id}>
          {!hideQuestionText && <p className="text-sm text-black">{label}</p>}
          <textarea
            {...field}
            {...register(field.name)}
            className={cn(
              "w-full min-h-[180px] flex flex-col gap-2 border px-4  pb-0 text-sm resize-none py-4",
              useNewStyles &&
                "font-medium bg-[#F4F4F4] rounded-2xl focus:outline-none transition-all duration-300 p-4 w-full",
              field.value?.length > 0
                ? "bg-white border-black border"
                : "border-none",
            )}
            onChange={(e) => {
              field.onChange(e);
              updateProgress(field.name, e.target.value);
            }}
            onBlur={() => {
              initiateAutosave(getValues());
            }}
            readOnly={isAdmin}
            disabled={isFormComplete}
          />
        </div>
      );
    case "CHECKBOX":
      return (
        <div className="flex flex-col w-full gap-1" key={id}>
          <div className="w-full min-h-10 flex gap-2 sm:flex-col lg:flex-row print:flex-row">
            <label className="w-full   items-center flex gap-3  sm:py-3 lg:py-0 print:py-0 cursor-pointer rounded-sm">
              <input
                {...field}
                {...register(field.name)}
                type="checkbox"
                onChange={(e) => {
                  field.onChange(e);
                  updateProgress(field.name, e.target.value);
                }}
                onBlur={() => {
                  initiateAutosave(getValues());
                }}
                readOnly={isAdmin}
                disabled={isFormComplete}
              />
              <p className="text-sm font-light">{label}</p>
            </label>
          </div>
        </div>
      );
    case "ADDRESS":
      return (
        <IntakeAddressInput
          isRequired={isRequired}
          questionId={id}
          questionText={questionText}
          field={field}
          register={register}
          errors={errors}
          getValues={getValues}
          setValue={setValue}
          trigger={trigger}
          control={control}
          updateProgress={updateProgress}
          isAdmin={isAdmin}
          initiateAutosave={initiateAutosave}
          disabled={isFormComplete}
        />
      );
    case "LIST":
      return (
        <IntakeListInput
          isAdminView={isAdmin}
          field={field}
          setValue={setValue}
          error={error}
          questionAttributes={question.questionAttributes}
          questionText={questionText}
          isRequired={isRequired}
          clearErrors={clearErrors}
          hideQuestionText={hideQuestionText}
        />
      );
    case "CLINICAL_PROVIDER":
      return (
        <IntakeClinicalProviderInput
          field={field}
          setValue={setValue}
          error={error}
          questionText={questionText}
          isRequired={isRequired}
          clearErrors={clearErrors}
          hideQuestionText={true}
          isAdminView={isAdmin}
          questionAttributes={question.questionAttributes}
          getValues={getValues}
        />
      );

    default:
      return <></>;
  }
};

export const formatResponseForCopy = (
  question: z.infer<typeof fieldSchema> & { originalAttributes?: any },
  response: IntakeFormRichResponse | string | string[] | undefined | any,
  useEnglish?: boolean,
) => {
  if (response === undefined || response === null) {
    return "";
  }
  const originalAttributes = {
    choices: Object.entries(
      (question as any).originalAttributes.choices || {},
    ).map(([key, choice]: [string, any], index: number) => {
      return {
        ...choice,
        id: (question.questionAttributes as { choices: IntakeQuestionChoice[] })
          .choices?.[index]?.id,
        key,
      };
    }),
  };

  switch (question.type) {
    case "SIGNATURE":
      return (response as IntakeFormRichResponse).isSignatureComplete
        ? "Signed"
        : "Not Signed";
    case "MULTI_SELECT": {
      const choices = useEnglish
        ? (originalAttributes as { choices: IntakeQuestionChoice[] }).choices
        : (question.questionAttributes as { choices: IntakeQuestionChoice[] })
            .choices;
      return choices
        .filter((c) => (response as string[]).includes(c.id))
        .map((c) => c.value)
        .join(", ");
    }
    case "SINGLE_SELECT": {
      const choices = useEnglish
        ? (originalAttributes as { choices: IntakeQuestionChoice[] }).choices
        : (question.questionAttributes as { choices: IntakeQuestionChoice[] })
            .choices;

      return (
        choices.find((c) => response.toString().includes(c.id))?.value ?? ""
      );
    }
    case "DATE": {
      if (isValid(parse(response as string, "yyyy-MM-dd", new Date()))) {
        return format(
          parse(response as string, "yyyy-MM-dd", new Date()),
          "MM/dd/yyyy",
        );
      }
      return response as string;
    }
    case "LIST": {
      if (typeof response === "string" && response.includes("[")) {
        // Check if response is empty or null before parsing
        if (!response || response === "" || response === "null") {
          console.warn(
            "Response:(",
            response,
            ") is empty or null in LIST for question",
            question,
          );
          return "N/A";
        }
        try {
          const json = JSON.parse(response);
          return (json as IntakeListEntry[])
            .map((item) => item.value)
            .join(", ");
        } catch (error) {
          console.warn(
            "Error parsing list response for question",
            question,
            error,
          );
          return response as string;
        }
      }
      if (Array.isArray(response)) {
        return response.map((item) => item.value).join(", ");
      }
      return response as string;
    }
    case "CLINICAL_PROVIDER": {
      if (typeof response === "string") {
        // Check if response is empty or null before parsing
        if (!response || response === "" || response === "null") {
          console.warn(
            "Response:(",
            response,
            ") is empty or null for Clinical Provider for question",
            question,
          );
          return "N/A";
        }
        try {
          const parsedValue = JSON.parse(response) as ClinicalProviderFormData;
          if (parsedValue.phone && isValidPhoneNumber(parsedValue.phone)) {
            parsedValue.phone = formatPhoneNumber(
              normalizePhoneInput(parsedValue.phone),
            );
          }
          if (parsedValue.name === "" && parsedValue.phone === "") {
            return "N/A";
          }
          const parts = [
            parsedValue.name,
            parsedValue.address.streetAddress,
            parsedValue.address.city,
            parsedValue.address.state,
            parsedValue.address.zip,
            parsedValue.phone,
          ].filter(Boolean);
          return parts.join(", ");
        } catch (error) {
          console.warn(
            "Error parsing clinical provider response for question",
            question,
            error,
          );
          return "N/A";
        }
      }
      if (typeof response === "object") {
        if (response.phone) {
          try {
            response.phone = formatPhoneNumber(
              normalizePhoneInput(response.phone),
            );
          } catch (e) {
            // shouldn't happen because if object then its from athena
            // but if it does,no-op, just return the original phone number
          }
        }
        if (response.name === "" && response.phone === "") {
          return "N/A";
        }
        const parts = [
          response.name,
          response.address.streetAddress,
          response.address.city,
          response.address.state,
          response.address.zip,
          response.phone,
        ].filter(Boolean);
        return parts.join(", ");
      }
      return response;
    }
    case "PHONE_NUMBER": {
      try {
        return formatPhoneNumber(normalizePhoneInput(response as string));
      } catch (e) {
        return response as string;
      }
    }

    default:
      return response as string;
  }
};

const ClipboardCopyIcon = ({ response }: { response: string }) => {
  return (
    <Files
      size={16}
      color="#6b7280"
      className="cursor-pointer mt-1"
      onClick={() => {
        navigator.clipboard.writeText(response);
        trackEvent({
          category: EVENT_CATEGORIES.INTERACTION,
          context: EVENT_CONTEXTS.APPOINTMENT,
          action: "copy_intake_question_clicked",
        });
      }}
    />
  );
};

/**
 * Need to refactor setValue / trigger because its propped drilled down so deep
 * @param param0
 * @returns
 */
export const IntakeFormQuestion = ({
  hiddenQuestions,
  setHiddenQuestions,
  setAnsweredQuestions,
  index,
  sectionName,
  sectionedFormQuestion,
  control,
  register,
  errors,
  clearErrors,
  questionRef,
  getValues,
  setValue,
  trigger,
  isAdmin,
  isPrint,
  initiateAutosave,
  isFormComplete,
}: {
  hiddenQuestions: Set<number>;
  setHiddenQuestions: React.Dispatch<React.SetStateAction<Set<number>>>;
  setAnsweredQuestions?: (newValue: number) => void;
  index: number;
  sectionName: string;
  sectionedFormQuestion: z.infer<typeof fieldSchema>[];
  control: Control<Partial<any>, any>;
  register: UseFormRegister<Partial<any>>;
  errors: FieldErrors<Partial<any>>;
  clearErrors: UseFormClearErrors<Partial<any>>;
  questionRef: React.MutableRefObject<RefMap> | null;
  getValues: UseFormGetValues<Partial<any>>;
  setValue: UseFormSetValue<Partial<any>>;
  trigger: UseFormTrigger<Partial<any>>;
  isAdmin: boolean;
  isPrint: boolean;
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void;
  isFormComplete?: boolean;
}) => {
  const t = useTranslations("IntakeFormStates");

  return (
    <div className="flex flex-col gap-4 mb-4" key={`${sectionName}${index}`}>
      <p className="text-base font-medium">{sectionName}</p>

      {sectionedFormQuestion.map((question, questionIndex) => {
        const physicalFormIndex = Number.parseInt(`${index}00${questionIndex}`);
        const currentQuestionRef =
          questionRef?.current[physicalFormIndex] ?? null;

        return hiddenQuestions.has(question.id) ? null : (
          <div key={question.id} className="print:break-inside-avoid">
            <div
              className="flex flex-col relative gap-0.5 w-full"
              ref={currentQuestionRef}
              style={{
                marginBottom: "12px",
                height: "fit-content",
              }}
              key={question.id}
            >
              <Controller
                name={question.id.toString()}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div className="flex items-start gap-1">
                    {formQuestionResolver(
                      hiddenQuestions,
                      setHiddenQuestions,
                      setAnsweredQuestions,
                      field,
                      error,
                      errors,
                      clearErrors,
                      question,
                      register,
                      getValues,
                      setValue,
                      trigger,
                      control,
                      isAdmin,
                      isPrint,
                      initiateAutosave,
                      isFormComplete,
                    )}
                    {isAdmin && !isPrint && (
                      <ClipboardCopyIcon
                        response={formatResponseForCopy(question, field.value)}
                      />
                    )}
                  </div>
                )}
              />
              {question.updatedAt && (
                <p className="text-xs font-light">
                  As of {format(question.updatedAt, "PPP")}
                </p>
              )}
              <p className="text-[#a92523] absolute font-light text-[10px] bottom-[-21px] mt-2">
                {errors[question.id.toString()]?.message &&
                  t(errors[question.id.toString()]?.message as string)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};
